import { inject, Injectable } from '@angular/core';
import { Movie, PageService } from '../Page/page.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { addDoc, collection, collectionData, deleteDoc, doc, DocumentReference, Firestore, getDocs, query, setDoc, where } from '@angular/fire/firestore';
import firebase from 'firebase/compat/app'
import { Timestamp } from 'firebase/firestore/lite';
import { Profile, Theater } from '../Auth/auth.service';
import moment from 'moment';

export class Booking {
    id: string
    userID: string
    Dx: number
    theater: string
    theaterListObj: Theater[]
    enableMultipleTheaters: boolean
    city: string
    state: string
    format: string
    admission: boolean
    startDate: Timestamp
    endDate: Timestamp
    created: Timestamp
    notes: string
    userName: string
    salesRep: string
    terms: string
    status: string
    movie: Movie | null
    profile: Profile | null


    constructor(json: any) {
        this.id = json.id
        this.Dx = json.Dx ? json.Dx : 0
        this.userID = json.userID ? json.userID : ''
        this.theater = json.theater
        this.enableMultipleTheaters = json.enableMultipleTheaters == true ? true : false
        this.theaterListObj = []
        if (json.theaterListObj) {
            this.theaterListObj = json.theaterListObj.map((theater: any) => new Theater(theater))
        }
        this.city = json.city
        this.state = json.state
        this.format = json.format
        this.admission = json.admission
        this.created = json.created
        this.startDate = json.startDate
        this.endDate = json.endDate
        this.notes = json.notes
        this.userName = json.userName
        this.terms = json.terms && json.terms != '' ? json.terms : "$100 vs 35%"
        this.status = json.status && json.status != '' ? json.status : "Pending"
        this.salesRep = json.salesRep ? json.salesRep : ''
        this.movie = json.movie ? json.movie : null
        this.profile = json.profile ? json.profile : null
    }

    getSave() {
        return {
            Dx: this.Dx,
            userID: this.userID,
            theater: this.theater,
            enableMultipleTheaters: this.enableMultipleTheaters,
            theaterListObj: this.theaterListObj.map((item) => item.getSave()),
            city: this.city,
            state: this.state,
            format: this.format,
            admission: this.admission,
            created: this.created,
            startDate: this.startDate,
            endDate: this.endDate,
            notes: this.notes,
            terms: this.terms,
            status: this.status,
            userName: this.userName,
            salesRep: this.salesRep,
        }
    }

    getCsv() {
        return {
            Dx: this.Dx,

            theater: this.theater,
            city: this.city,
            state: this.state,
            format: this.format,
            admission: this.admission,
            created: this.created ? moment(this.created.toDate()) : 'N/A',
            startDate: this.startDate ? moment(this.startDate.toDate()) : 'N/A',
            endDate: this.endDate ? moment(this.endDate.toDate()) : 'N/A',
            notes: this.notes,
            terms: this.terms,
            status: this.status,
            userName: this.userName,
            salesRep: this.salesRep
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class BookingService {
    public isBookingOpen: boolean = false;
    public isBookingOpen$: BehaviorSubject<boolean>
    public movie: Movie | null = null

    public isDashBookingOpen: boolean = false
    public isDashBookingOpen$: BehaviorSubject<boolean>
    public dashBooking: Booking | null = null

    private firestore = inject(Firestore);

    constructor(
        private pageService: PageService
    ) {
        this.isBookingOpen$ = new BehaviorSubject<boolean>(false)
        this.isDashBookingOpen$ = new BehaviorSubject<boolean>(false)
    }

    open(movie: Movie) {
        this.pageService.getMoviesByIds([movie.Dx]).then((movies) => {
            this.movie = movies[0]
            this.isBookingOpen = true;
            this.isBookingOpen$.next(this.isBookingOpen)
        })
    }

    close() {
        this.movie = null
        this.isBookingOpen = false;
        this.isBookingOpen$.next(this.isBookingOpen)
    }

    dashOpen(booking: Booking) {
        // return
        this.dashBooking = booking
        this.isDashBookingOpen = true;
        this.isDashBookingOpen$.next(this.isDashBookingOpen)
    }

    dashClose() {
        this.dashBooking = null
        this.isDashBookingOpen = false;
        this.isDashBookingOpen$.next(this.isDashBookingOpen)
    }

    async getPending(ids: string[]): Promise<Booking[]> {
        if (!ids || ids.length === 0) {
            return [];
        }
        if (ids.length > 20) {
            // ids = ids.slice(0, 20)
            ids = ids.slice(-20)
        }
        const moviesCollection = collection(this.firestore, 'new_booking');
        const q = query(moviesCollection, where(firebase.firestore.FieldPath.documentId(), 'in', ids));
        const querySnapshot = await getDocs(q);

        let unsorted = querySnapshot.docs.map(doc => new Booking(doc.data()));

        return unsorted
    }

    getPendingAll(pageSize: number = 20, lastDate?: Timestamp): Observable<Booking[]> {
        const BookingCollection = collection(this.firestore, 'new_booking');

        // get documents (data) from the collection using collectionData
        return collectionData(BookingCollection, { idField: 'id' }) as Observable<Booking[]>;
    }

    getApprovedAll(pageSize: number = 20, lastDate?: Timestamp): Observable<Booking[]> {
        const BookingCollection = collection(this.firestore, 'approved_booking');

        // get documents (data) from the collection using collectionData
        return collectionData(BookingCollection, { idField: 'id' }) as Observable<Booking[]>;
    }

    getDeniedAll(pageSize: number = 20, lastDate?: Timestamp): Observable<Booking[]> {
        const BookingCollection = collection(this.firestore, 'denied_booking');

        // get documents (data) from the collection using collectionData
        return collectionData(BookingCollection, { idField: 'id' }) as Observable<Booking[]>;
    }

    async getApproved(ids: string[]): Promise<Booking[]> {
        if (!ids || ids.length === 0) {
            return [];
        }
        const moviesCollection = collection(this.firestore, 'approved_booking');

        if (ids.length > 20) {
            ids = ids.slice(-20)
        }

        const q = query(moviesCollection, where(firebase.firestore.FieldPath.documentId(), 'in', ids));
        const querySnapshot = await getDocs(q);

        let unsorted = querySnapshot.docs.map(doc => new Booking(doc.data()));

        return unsorted
    }

    async onSubmitBooking(booking: Booking, uid: string): Promise<string | null> {
        if (!this.movie) return null;

        booking.userID = uid
        booking.Dx = this.movie?.Dx

        // const collectionRef = collection(this.firestore, 'approved_booking'); // Replace 'myCollection' with your collection name
        // // const collectionRef = collection(this.firestore, 'new_booking'); // Replace 'myCollection' with your collection name
        // await addDoc(collectionRef, booking.getSave())
        //     .then((docRef: DocumentReference) => {
        //         console.log('Document written with ID: ', docRef.id);
        //     })
        //     .catch((error) => {
        //         console.error('Error adding document: ', error);
        //     })


        try {
            const collectionRef = collection(this.firestore, 'new_booking');
            const docRef: DocumentReference = await addDoc(collectionRef, booking.getSave());
            console.log('Document written with ID: ', docRef.id);
            // send mail
            await fetch('https://lionsgaterepertory.com/server/booking-approved?id=' + docRef.id, {
                // await fetch('http://localhost:3000/booking-approved?id=' + docRef.id, {
                method: 'GET', // Specify the HTTP method
                headers: {
                    'Content-Type': 'application/json', // Set the content type for the request body
                },
            })
            // email sent

            return docRef.id; // <-- Return the document ID
        } catch (error) {
            console.error('Error adding document: ', error);
            return null;
        }
    }

    async onApproveBooking(b: Booking): Promise<string | null> {
        try {
            b.status = "Approved"

            let booking: Booking = new Booking(b)

            const docRef1 = doc(this.firestore, 'new_booking', booking.id);
            try {
                await deleteDoc(docRef1); // Delete the document
                console.log('Document successfully deleted!');
            } catch (error) {
                console.error('Error removing document: ', error);
            }

            const collectionRef = collection(this.firestore, 'approved_booking');

            const newReqDocRef = doc(this.firestore, `approved_booking/${booking.id}`);

            // const docRef: DocumentReference = await addDoc(collectionRef, booking.getSave());
            await setDoc(newReqDocRef, booking.getSave());

            console.log('Document written with ID: ', booking.id);
            return booking.id; // <-- Return the document ID
        } catch (error) {
            console.error('Error adding document: ', error);
            return null;
        }
    }

    async onDenyBooking(b: Booking): Promise<string | null> {
        try {
            b.status = "Denied"

            let booking: Booking = new Booking(b)

            const docRef1 = doc(this.firestore, 'new_booking', booking.id);
            try {
                await deleteDoc(docRef1); // Delete the document
                console.log('Document successfully deleted!');
            } catch (error) {
                console.error('Error removing document: ', error);
            }

            const collectionRef = collection(this.firestore, 'denied_booking');

            const docRef: DocumentReference = await addDoc(collectionRef, booking.getSave());

            console.log('Document written with ID: ', docRef.id);
            return docRef.id; // <-- Return the document ID
        } catch (error) {
            console.error('Error adding document: ', error);
            return null;
        }
    }
}
